import React from 'react';
import ReactDOM from 'react-dom/client';
import './App.css';
import './utils/journeyProgress'; // FU6: runs one-shot localStorage migration (v1 numeric → v2 slug)
import App from './App';
import { registerServiceWorker } from './serviceWorkerRegistration';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID?.trim();
const ATTRIBUTION_KEY = 'eliteedge:analytics-attribution';
const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const PENDING_SIGN_UP_KEY = 'eliteedge:pending-sign-up';

function persistAttribution(attribution) {
  const persisted = {
    attribution_model: 'first_touch_30d',
    ...attribution,
  };
  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({
      capturedAt: Date.now(),
      attribution: persisted,
    }));
  } catch {
    // The event can still be sent without persisting attribution.
  }
  return persisted;
}

function readAttribution() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || 'null');
    if (
      stored?.attribution
      && Number.isFinite(stored.capturedAt)
      && Date.now() - stored.capturedAt <= ATTRIBUTION_MAX_AGE_MS
    ) {
      return stored.attribution;
    }
    window.localStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // Analytics must never interrupt the product when storage is unavailable.
  }

  const params = new URLSearchParams(window.location.search);
  const campaignFields = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
  };
  const taggedCampaign = Object.values(campaignFields).some(Boolean);

  if (taggedCampaign) {
    const attribution = { attribution_status: 'utm_tagged' };
    Object.entries(campaignFields).forEach(([key, value]) => {
      if (value) attribution[key] = value.slice(0, 100);
    });
    return persistAttribution(attribution);
  }

  if (window.location.pathname === '/about' && !document.referrer) {
    return persistAttribution({
      attribution_status: 'unattributed_direct',
      possible_print_qr_first_touch: true,
    });
  }

  return { attribution_status: 'unattributed' };
}

function trackAnalyticsEvent(event, details = {}) {
  if (!event || typeof window.gtag !== 'function') return;
  window.gtag('event', event, {
    ...readAttribution(),
    ...details,
  });
}

function initializeGoogleAnalytics() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  window.eliteEdgeTrack = trackAnalyticsEvent;

  window.addEventListener('eliteedge:analytics', ({ detail }) => {
    if (!detail?.event) return;
    const { event, ...details } = detail;
    trackAnalyticsEvent(event, details);
  });
  window.addEventListener('eliteedge:marketing', ({ detail }) => {
    if (!detail?.event) return;
    const { event, ...details } = detail;
    trackAnalyticsEvent(`about_${event}`, details);
  });

  if (GA_MEASUREMENT_ID) {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  }

  try {
    const pendingMethod = window.sessionStorage.getItem(PENDING_SIGN_UP_KEY);
    const oauthCompleted = new URLSearchParams(window.location.search).get('oauth') === '1';
    if (oauthCompleted) {
      if (pendingMethod) {
        trackAnalyticsEvent('sign_up', { method: pendingMethod });
        window.sessionStorage.removeItem(PENDING_SIGN_UP_KEY);
      }
      trackAnalyticsEvent('login_success', { method: pendingMethod || 'oauth' });
    }
  } catch {
    // OAuth completion continues even when browser storage is unavailable.
  }
}

initializeGoogleAnalytics();

window.addEventListener('load', () => {
  registerServiceWorker();
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
